// ─────────────────────────────────────────────────────────────
// Dashboard Screen — Admin overview with stats, filters, activity
// ─────────────────────────────────────────────────────────────
import { ref, get, query, orderByChild,
         limitToLast, startAt, endAt,
         equalTo }                      from 'firebase/database';
import { db }                           from '@/firebase';
import { toastError }                   from '@/ui/toast';
import { fmt, sanitize }                from '@/helpers';

interface DailyStats  { [action: string]: number }
interface AdminStats  { totals: Record<string, number>; daily: Record<string, DailyStats> }
interface LogEntry    { level: string; action: string; message: string; email: string | null; timestamp: number }

// ── Dashboard filters state ───────────────────────────────────
let dashPeriod: 7 | 14 | 30 = 7;

export function setDashPeriod(days: number): void {
  dashPeriod = days as 7 | 14 | 30;
  renderDashboard();
}

// ── Main render ───────────────────────────────────────────────
export async function renderDashboard(): Promise<void> {
  const el = document.getElementById('dashboard-content');
  if (!el) return;
  el.innerHTML = `<div class="empty">Carregando dados...</div>`;

  try {
    // Read users and stats (admin-accessible paths)
    const [usersSnap, statsSnap] = await Promise.all([
      get(ref(db, 'users')),
      get(ref(db, 'adminStats')),
    ]);

    const users  = (usersSnap.val()  ?? {}) as Record<string, { displayName?: string; email: string; role?: string }>;
    const stats  = (statsSnap.val()  ?? { totals: {}, daily: {} }) as AdminStats;

    const totalUsers = Object.keys(users).length;
    const adminUsers = Object.values(users).filter(u => u.role === 'admin').length;

    // Count unique trips from adminStats (trip_created counter)
    const totalTripsCreated = stats.totals?.trip_created ?? 0;

    // Chart data for selected period
    const chartData = buildChartData(stats.daily ?? {}, dashPeriod);

    // Compute period totals from chart data
    const periodLogins  = chartData.logins.reduce((a, b) => a + b, 0);
    const periodViews   = chartData.pageViews.reduce((a, b) => a + b, 0);
    const periodTrips   = chartData.trips.reduce((a, b) => a + b, 0);
    const periodErrors  = chartData.errors.reduce((a, b) => a + b, 0);
    const periodActions = chartData.actions.reduce((a, b) => a + b, 0);

    el.innerHTML = `
      <!-- Period filter -->
      <div style="display:flex;gap:6px;margin-bottom:14px;flex-wrap:wrap;align-items:center">
        <span style="font-size:12px;color:var(--muted);font-weight:500">Período:</span>
        ${[7, 14, 30].map(d => `
          <button class="tab-pill ${dashPeriod === d ? 'active' : ''}"
            style="padding:4px 12px;font-size:12px"
            onclick="window.__setDashPeriod(${d})">
            ${d} dias
          </button>`).join('')}
        <button class="dash-refresh-btn" onclick="window.__refreshDashboard()" style="margin-left:auto">↻ Atualizar</button>
      </div>

      <!-- KPI Cards -->
      <div class="dash-grid">
        ${kpiCard('👥', 'Usuários',        totalUsers,         `${adminUsers} admin`,         'var(--lux-blue)')}
        ${kpiCard('✈️', 'Viagens criadas', totalTripsCreated,  'histórico total',             '#1E9E5E')}
        ${kpiCard('🔑', 'Logins',          periodLogins,       `últimos ${dashPeriod} dias`,  '#D97706')}
        ${kpiCard('👁️', 'Páginas vistas', periodViews,        `últimos ${dashPeriod} dias`,  '#6D5FD5')}
        ${kpiCard('⚡', 'Ações',           periodActions,      `últimos ${dashPeriod} dias`,  '#6D5FD5')}
        ${kpiCard('🗺️', 'Novas viagens',  periodTrips,        `últimos ${dashPeriod} dias`,  '#0891B2')}
        ${kpiCard('⚠️', 'Erros',          periodErrors,       `últimos ${dashPeriod} dias`,  'var(--lux-red)')}
      </div>

      <!-- Activity chart -->
      <div class="card" style="margin-top:12px">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:12px;flex-wrap:wrap;gap:8px">
          <div class="card-title">Atividade — últimos ${dashPeriod} dias</div>
          <div style="display:flex;gap:6px;flex-wrap:wrap">
            <label style="display:flex;align-items:center;gap:4px;font-size:12px;color:var(--muted);cursor:pointer">
              <input type="checkbox" id="dash-show-logins"  checked onchange="window.__updateDashChart()" style="accent-color:var(--lux-blue)"> Logins
            </label>
            <label style="display:flex;align-items:center;gap:4px;font-size:12px;color:var(--muted);cursor:pointer">
              <input type="checkbox" id="dash-show-views" checked onchange="window.__updateDashChart()" style="accent-color:#6D5FD5"> Páginas
            </label>
            <label style="display:flex;align-items:center;gap:4px;font-size:12px;color:var(--muted);cursor:pointer">
              <input type="checkbox" id="dash-show-trips"   checked onchange="window.__updateDashChart()" style="accent-color:#1E9E5E"> Viagens
            </label>
            <label style="display:flex;align-items:center;gap:4px;font-size:12px;color:var(--muted);cursor:pointer">
              <input type="checkbox" id="dash-show-errors"  checked onchange="window.__updateDashChart()" style="accent-color:var(--lux-red)"> Erros
            </label>
          </div>
        </div>
        <div style="overflow-x:auto;height:160px">
          <canvas id="dash-chart"></canvas>
        </div>
      </div>

      <!-- Users table -->
      <div class="card" style="margin-top:12px">
        <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:10px;flex-wrap:wrap;gap:8px">
          <div class="card-title">👥 Usuários cadastrados</div>
          <input type="text" id="dash-user-search" placeholder="Buscar usuário..."
            oninput="window.__filterDashUsers()"
            style="padding:6px 10px;border:1px solid var(--border2);border-radius:var(--r);font-size:12px;background:var(--bg);color:var(--text);font-family:var(--font);outline:none;width:200px">
        </div>
        <div id="dash-users-table">${renderUsersTable(users)}</div>
      </div>`;

    // Store data for chart interactivity
    (window as unknown as Record<string, unknown>)['__dashChartData'] = chartData;

    setTimeout(() => renderActivityChart(chartData), 50);

  } catch (e) {
    toastError('Erro ao carregar dashboard: ' + (e instanceof Error ? e.message : String(e)));
    el.innerHTML = '<div class="empty">Erro ao carregar dados. Verifique as regras do Firebase.</div>';
  }
}

// ── Filter dashboard users table ──────────────────────────────
export function filterDashUsers(): void {
  const q   = ((document.getElementById('dash-user-search') as HTMLInputElement)?.value ?? '').toLowerCase();
  const tbl = document.getElementById('dash-users-table');
  if (!tbl) return;
  tbl.querySelectorAll('tbody tr').forEach(row => {
    const text = row.textContent?.toLowerCase() ?? '';
    (row as HTMLElement).style.display = !q || text.includes(q) ? '' : 'none';
  });
}

// ── Update chart series visibility ───────────────────────────
let _dashChart: unknown = null;
export function updateDashChart(): void {
  const chart = _dashChart as { data: { datasets: { hidden: boolean }[] }; update: () => void } | null;
  if (!chart) return;
  const showLogins = (document.getElementById('dash-show-logins') as HTMLInputElement)?.checked;
  const showViews  = (document.getElementById('dash-show-views')  as HTMLInputElement)?.checked;
  const showTrips  = (document.getElementById('dash-show-trips')  as HTMLInputElement)?.checked;
  const showErrors = (document.getElementById('dash-show-errors') as HTMLInputElement)?.checked;
  chart.data.datasets[0].hidden = !showLogins;
  chart.data.datasets[1].hidden = !showViews;
  chart.data.datasets[2].hidden = !showTrips;
  chart.data.datasets[3].hidden = !showErrors;
  chart.update();
}

// ── KPI card ──────────────────────────────────────────────────
function kpiCard(icon: string, label: string, value: number, sub: string, accent: string): string {
  return `
    <div class="dash-kpi" style="border-left-color:${accent}">
      <div class="dash-kpi-icon">${icon}</div>
      <div>
        <div class="dash-kpi-value">${value.toLocaleString('pt-BR')}</div>
        <div class="dash-kpi-label">${label}</div>
        <div class="dash-kpi-sub">${sub}</div>
      </div>
    </div>`;
}

// ── Users table ───────────────────────────────────────────────
function renderUsersTable(
  users: Record<string, { displayName?: string; email: string; role?: string }>
): string {
  const sorted = Object.entries(users).sort(([, a], [, b]) =>
    (a.displayName || a.email).localeCompare(b.displayName || b.email, 'pt-BR')
  );
  if (!sorted.length) return '<div class="empty">Nenhum usuário.</div>';
  return `
    <table class="dash-table">
      <thead><tr><th>Nome</th><th>E-mail</th><th>Perfil</th></tr></thead>
      <tbody>
        ${sorted.map(([, u]) => `
          <tr>
            <td>${sanitize(u.displayName ?? '—')}</td>
            <td style="max-width:180px;overflow:hidden;text-overflow:ellipsis;white-space:nowrap">${sanitize(u.email)}</td>
            <td>${u.role === 'admin' ? '<span class="badge b-info">Admin</span>' : '<span class="badge">Usuário</span>'}</td>
          </tr>`).join('')}
      </tbody>
    </table>`;
}

// ── Build chart data ──────────────────────────────────────────
function buildChartData(daily: Record<string, DailyStats>, days: number) {
  const dayList: string[] = [];
  for (let i = days - 1; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    dayList.push(d.toISOString().slice(0, 10));
  }
  return {
    labels:  dayList.map(d => fmt(d)),
    logins:  dayList.map(d => daily[d]?.login ?? 0),
    pageViews: dayList.map(d => daily[d]?.page_view ?? 0),
    trips:   dayList.map(d => (daily[d]?.trip_created ?? 0)),
    errors:  dayList.map(d => (daily[d]?.firebase_error ?? 0) + (daily[d]?.permission_denied ?? 0) + (daily[d]?.unknown_error ?? 0)),
    actions: dayList.map(d => Object.entries(daily[d] ?? {})
      .filter(([action]) => action !== 'page_view')
      .reduce((sum, [, count]) => sum + count, 0)),
  };
}

// ── Render chart ──────────────────────────────────────────────
async function renderActivityChart(data: ReturnType<typeof buildChartData>): Promise<void> {
  const canvas = document.getElementById('dash-chart') as HTMLCanvasElement | null;
  if (!canvas) return;
  const { Chart, registerables } = await import('chart.js');
  Chart.register(...registerables);
  // Destroy existing chart if any
  const existing = Chart.getChart(canvas);
  if (existing) existing.destroy();

  const chart = new Chart(canvas, {
    type: 'bar',
    data: {
      labels: data.labels,
      datasets: [
        { label: 'Logins',  data: data.logins, backgroundColor: '#00A3E0', borderRadius: 4, borderSkipped: false },
        { label: 'Páginas', data: data.pageViews, backgroundColor: '#6D5FD5', borderRadius: 4, borderSkipped: false },
        { label: 'Viagens', data: data.trips,  backgroundColor: '#1E9E5E', borderRadius: 4, borderSkipped: false },
        { label: 'Erros',   data: data.errors, backgroundColor: '#EF3340', borderRadius: 4, borderSkipped: false },
      ],
    },
    options: {
      responsive:          true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'bottom', labels: { font: { size: 11 }, boxWidth: 12, padding: 16 } },
        tooltip: { callbacks: { label: ctx => ` ${ctx.dataset.label}: ${ctx.parsed.y}` } },
      },
      scales: {
        x: { grid: { display: false }, ticks: { font: { size: 10 } } },
        y: { beginAtZero: true, ticks: { precision: 0, font: { size: 10 } } },
      },
    },
  });
  _dashChart = chart;
}
